/**
 * PocketFiler - Keyboard Shortcuts + Command Palette
 * Global shortcuts: Cmd/Ctrl+K (palette), Cmd+D (dashboard), etc.
 * Press ? to see all shortcuts.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import {
  Search, X, Home, FileText, Users, Briefcase, Calendar,
  Settings, HelpCircle, BarChart3, Zap, ArrowRight, Keyboard
} from 'lucide-react';


const COMMANDS = [
  { key: 'dashboard', label: 'Go to Dashboard', icon: Home, route: '/dashboard', shortcut: 'D' },
  { key: 'deals', label: 'Go to Deals', icon: Briefcase, route: '/deals' },
  { key: 'documents', label: 'Go to Documents', icon: FileText, route: '/documents' },
  { key: 'contracts', label: 'Go to Contracts', icon: FileText, route: '/contract-templates' },
  { key: 'clients', label: 'Go to Clients', icon: Users, route: '/clients' },
  { key: 'meetings', label: 'Go to Meetings', icon: Calendar, route: '/meetings' },
  { key: 'analytics', label: 'Go to Analytics', icon: BarChart3, route: '/analytics' },
  { key: 'settings', label: 'Settings', icon: Settings, route: '/settings', shortcut: ',' },
  { key: 'help', label: 'Help Center', icon: HelpCircle, route: '/help' },
  { key: 'create-document', label: 'Create new document', icon: FileText, route: '/documents/create', shortcut: 'N' },
  { key: 'create-contract', label: 'Generate new contract', icon: Zap, route: '/contract-templates' },
];


const SHORTCUTS_HELP = [
  { keys: 'Cmd+K / Ctrl+K', action: 'Open command palette' },
  { keys: '?', action: 'Show this help' },
  { keys: 'Cmd+D', action: 'Go to Dashboard' },
  { keys: 'Cmd+N', action: 'New document' },
  { keys: 'Cmd+,', action: 'Open Settings' },
  { keys: 'Esc', action: 'Close modals / palette' },
];


export default function KeyboardShortcuts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return COMMANDS;
    const q = query.toLowerCase();
    return COMMANDS.filter(c => c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q));
  }, [query]);

  // Global key listener
  useEffect(() => {
    const handler = (e) => {
      // Ignore if typing in input/textarea/contenteditable
      const tag = e.target?.tagName?.toLowerCase();
      const editable = e.target?.isContentEditable;
      const isTyping = tag === 'input' || tag === 'textarea' || editable;

      // Cmd/Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
        setHelpOpen(false);
        return;
      }

      // Esc
      if (e.key === 'Escape') {
        setOpen(false);
        setHelpOpen(false);
        return;
      }

      // Don't fire other shortcuts while typing
      if (isTyping) return;

      // ?
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }

      // Cmd+D → Dashboard
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        navigate('/dashboard');
        return;
      }

      // Cmd+N → New document
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        navigate('/documents/create');
        return;
      }

      // Cmd+, → Settings
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        navigate('/settings');
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Focus input when palette opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSelectedIndex(0);
    }
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Arrow navigation in palette
  const handleInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[selectedIndex];
      if (cmd) {
        setOpen(false);
        navigate(cmd.route);
      }
    }
  };

  return (
    <>
      {/* Command Palette */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 flex items-start justify-center pt-[15vh] p-4 animate-in fade-in"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-background rounded-xl shadow-2xl border overflow-hidden animate-in slide-in-from-top-4"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
                onKeyDown={handleInputKey}
                placeholder={t('shortcuts.searchPlaceholder') || 'Search pages or actions...'}
                className="flex-1 bg-transparent outline-none text-sm"
              />
              <kbd className="text-[10px] text-muted-foreground font-mono px-1.5 py-0.5 rounded bg-muted">Esc</kbd>
            </div>

            {/* Results */}
            <div className="max-h-[50vh] overflow-y-auto py-2">
              {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No results for "{query}"
                </div>
              ) : (
                filtered.map((cmd, i) => {
                  const Icon = cmd.icon;
                  return (
                    <button
                      key={cmd.key}
                      onClick={() => { setOpen(false); navigate(cmd.route); }}
                      onMouseEnter={() => setSelectedIndex(i)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${
                        i === selectedIndex ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span>{cmd.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {cmd.shortcut && (
                          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+{cmd.shortcut}
                          </kbd>
                        )}
                        {i === selectedIndex && <ArrowRight className="h-3.5 w-3.5 text-primary" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t flex items-center justify-between text-[10px] text-muted-foreground">
              <span>Press <kbd className="font-mono px-1 rounded bg-muted">?</kbd> for all shortcuts</span>
              <span>
                <kbd className="font-mono px-1 rounded bg-muted">↑↓</kbd> navigate · <kbd className="font-mono px-1 rounded bg-muted">↵</kbd> select
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Shortcuts Help Modal */}
      {helpOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setHelpOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-background rounded-xl shadow-2xl border overflow-hidden animate-in slide-in-from-bottom-4"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Keyboard className="h-4 w-4 text-primary" />
                {t('shortcuts.title') || 'Keyboard Shortcuts'}
              </h3>
              <button onClick={() => setHelpOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {SHORTCUTS_HELP.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-muted-foreground">{s.action}</span>
                  <kbd className="font-mono text-xs px-2 py-1 rounded bg-muted">{s.keys}</kbd>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
